import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * @context CookieConsentContext
 * @description Gestión de consentimiento de cookies y cumplimiento normativo
 * Especificaciones: GDPR-like compliance, localStorage persistence, conditional script loading
 */

export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export interface CookieConsentContextType {
  consent: CookieConsent;
  hasConsented: boolean;
  showBanner: boolean;
  updateConsent: (newConsent: Partial<CookieConsent>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  resetConsent: () => void;
  hideBanner: () => void;
}

const defaultConsent: CookieConsent = {
  essential: true, // Siempre activas
  analytics: false,
  marketing: false,
  preferences: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_STORAGE_KEY = 'nfcores_cookie_consent';
const CONSENT_VERSION = '1.0';

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent>(defaultConsent);
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // Cargar consentimiento desde localStorage al inicializar
  useEffect(() => {
    try {
      const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (storedConsent) {
        const parsed = JSON.parse(storedConsent);
        
        // Verificar versión del consentimiento
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed.consent);
          setHasConsented(true);
          setShowBanner(false);
        } else {
          // Nueva versión, mostrar banner nuevamente
          setShowBanner(true);
        }
      } else {
        // Primera visita, mostrar banner
        setShowBanner(true);
      }
    } catch (error) {
      console.error('Error loading cookie consent:', error);
      setShowBanner(true);
    }
  }, []);

  // Guardar consentimiento en localStorage
  const saveConsent = (newConsent: CookieConsent) => {
    try {
      const consentData = {
        consent: newConsent,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  };

  // Actualizar consentimiento
  const updateConsent = (newConsent: Partial<CookieConsent>) => {
    const updatedConsent = {
      ...consent,
      ...newConsent,
      essential: true, // Las esenciales siempre están activas
    };
    
    setConsent(updatedConsent);
    setHasConsented(true);
    saveConsent(updatedConsent);
    
    // Cargar/descargar scripts según el consentimiento
    loadConditionalScripts(updatedConsent);
  };

  // Aceptar todas las cookies
  const acceptAll = () => {
    const allAccepted: CookieConsent = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    
    updateConsent(allAccepted);
    setShowBanner(false);
  };

  // Rechazar todas las cookies opcionales
  const rejectAll = () => {
    const onlyEssential: CookieConsent = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    
    updateConsent(onlyEssential);
    setShowBanner(false);
  };

  // Resetear consentimiento (para testing)
  const resetConsent = () => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    setConsent(defaultConsent);
    setHasConsented(false);
    setShowBanner(true);
  };

  // Ocultar banner
  const hideBanner = () => {
    setShowBanner(false);
  };

  // Cargar scripts condicionales según el consentimiento
  const loadConditionalScripts = (consentData: CookieConsent) => {
    // Google Analytics
    if (consentData.analytics) {
      loadGoogleAnalytics();
    } else {
      removeGoogleAnalytics();
    }

    // Scripts de marketing
    if (consentData.marketing) {
      loadMarketingScripts();
    } else {
      removeMarketingScripts();
    }

    // Scripts de preferencias
    if (consentData.preferences) {
      loadPreferenceScripts();
    }
  };

  // Cargar Google Analytics
  const loadGoogleAnalytics = () => {
    if (typeof window !== 'undefined' && !window.gtag) {
      const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
      
      if (GA_MEASUREMENT_ID) {
        // Cargar script de Google Analytics
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);

        // Configurar gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', GA_MEASUREMENT_ID, {
          anonymize_ip: true,
          cookie_flags: 'SameSite=None;Secure',
        });

        console.log('✅ Google Analytics loaded with consent');
      }
    }
  };

  // Remover Google Analytics
  const removeGoogleAnalytics = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      // Deshabilitar Google Analytics
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
      console.log('🚫 Google Analytics disabled');
    }
  };

  // Cargar scripts de marketing
  const loadMarketingScripts = () => {
    console.log('📢 Marketing scripts would be loaded here');
    // Aquí se cargarían scripts como Facebook Pixel, etc.
  };

  // Remover scripts de marketing
  const removeMarketingScripts = () => {
    console.log('🚫 Marketing scripts disabled');
  };

  // Cargar scripts de preferencias
  const loadPreferenceScripts = () => {
    console.log('⚙️ Preference scripts would be loaded here');
    // Scripts para recordar preferencias del usuario
  };

  const value: CookieConsentContextType = {
    consent,
    hasConsented,
    showBanner,
    updateConsent,
    acceptAll,
    rejectAll,
    resetConsent,
    hideBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Hook para usar el contexto
export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Tipos para window.gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
